import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useDeepLinkHighlight from '../hooks/useDeepLinkHighlight';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  ChevronDown, 
  ChevronRight, 
  Trash2, 
  Upload,
  Building2,
  Loader2,
  Settings2,
  CircleDot,
  Pencil
} from 'lucide-react';
import { format } from 'date-fns';
import { 
  FilterBar, 
  SelectFilter, 
  SearchFilter,
  countActiveFilters 
} from '../components/FilterComponents';
import { ExportButton } from '../utils/exportUtils';
import DeleteRequestDialog from '../components/DeleteRequestDialog';
import PendingDeletionsPanel from '../components/PendingDeletionsPanel';
import SearchableSelect from '../components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default vehicle categories (can be extended via UI)
const DEFAULT_VEHICLE_CATEGORIES = [
  'Trucks',
  'Dozers',
  'Excavators',
  'Graders',
  'Loaders',
  'LMV',
  'LMV-Trucks',
  'Surface Miner'
];

// Categories that have tyres (not tracks/chains)
const TYRE_CATEGORIES = ['Trucks', 'Graders', 'Loaders', 'LMV', 'LMV-Trucks'];

// Generate year options (last 30 years)
const YEAR_OPTIONS = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i);

function generatePositionsPreview(axleConfig) {
  if (!axleConfig || axleConfig.length === 0) return [];
  const positions = [];
  for (const axle of axleConfig) {
    if (axle.tyre_type === 'Double') {
      positions.push(
        `${axle.position}-Left-Outer`,
        `${axle.position}-Left-Inner`,
        `${axle.position}-Right-Inner`,
        `${axle.position}-Right-Outer`
      );
    } else {
      positions.push(`${axle.position}-Left`, `${axle.position}-Right`);
    }
  }
  return positions;
}

const AssetManagement = ({ user }) => {
  const canEdit = user?.is_admin || user?.permissions?.assets?.edit;
  const canDelete = user?.is_admin || user?.is_super_admin;

  // Delete approval
  const canApprove = user?.is_admin || user?.is_super_admin;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingDelRequests, setPendingDelRequests] = useState([]);

  const fetchPendingDeletions = async () => {
    if (!canApprove) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests?status=pending&module=assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingDelRequests(res.data);
    } catch {}
  };

  const handleApproveDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deletion approved');
      fetchData();
      fetchPendingDeletions();
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Request rejected');
      fetchPendingDeletions();
    } catch { toast.error('Failed to reject'); }
  };

  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSites, setExpandedSites] = useState({});

  useDeepLinkHighlight('door', 'asset-row-', [loading, assets.length, expandedSites]);

  // Deep-link: expand the site that contains the target door_no
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('door');
    if (!target || assets.length === 0) return;
    const asset = assets.find(a => a.door_no === target);
    if (!asset) return;
    const sid = asset.site_id;
    if (sid && !expandedSites[sid]) {
      setExpandedSites(prev => ({ ...prev, [sid]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);
  const [isAddSiteOpen, setIsAddSiteOpen] = useState(false);
  const [isEditSiteOpen, setIsEditSiteOpen] = useState(false);
  const [isAddAssetOpen, setIsAddAssetOpen] = useState(false);
  const [isEditAssetOpen, setIsEditAssetOpen] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [editingSite, setEditingSite] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);

  // Custom categories (persisted in localStorage)
  const [customCategories, setCustomCategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lsn_custom_categories') || '[]'); }
    catch (_e) { return []; }
  });
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);

  const VEHICLE_CATEGORIES = useMemo(() => {
    const all = [...DEFAULT_VEHICLE_CATEGORIES, ...customCategories];
    return [...new Set(all)];
  }, [customCategories]);

  const handleAddCategory = () => {
    const val = newCategoryInput.trim();
    if (!val || VEHICLE_CATEGORIES.includes(val)) return;
    const updated = [...customCategories, val];
    setCustomCategories(updated);
    localStorage.setItem('lsn_custom_categories', JSON.stringify(updated));
    setAssetForm(prev => ({ ...prev, vehicle_category: val }));
    setNewCategoryInput('');
    setShowAddCategory(false);
    toast.success(`Category "${val}" added`);
  };

  const handleDeleteCategory = (cat) => {
    if (DEFAULT_VEHICLE_CATEGORIES.includes(cat)) { toast.error("Can't remove default categories"); return; }
    const updated = customCategories.filter(c => c !== cat);
    setCustomCategories(updated);
    localStorage.setItem('lsn_custom_categories', JSON.stringify(updated));
    toast.success(`Category "${cat}" removed`);
  };

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    make: '',
    site: ''
  });

  const resetFilters = () => {
    setFilters({ search: '', category: '', make: '', site: '' });
  };

  const [siteForm, setSiteForm] = useState({ name: '', location: '' });
  const [assetForm, setAssetForm] = useState({
    site_id: '',
    vehicle_category: '',
    make: '',
    model: '',
    manufacturing_year: '',
    door_no: '',
    date_of_purchase: '',
    cum_per_trip: '',
    axle_config: []
  });
  
  // Template management
  const [templateForm, setTemplateForm] = useState({
    vehicle_category: '',
    make: '',
    model: '',
    axle_config: []
  });

  useEffect(() => {
    fetchData();
    fetchPendingDeletions();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [sitesRes, assetsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/sites`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vehicle-templates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSites(sitesRes.data);
      setAssets(assetsRes.data);
      setTemplates(templatesRes.data);
      
      if (sitesRes.data.length > 0) {
        setExpandedSites({ [sitesRes.data[0].id]: true });
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Check for matching template when make/model changes
  const checkTemplate = async (category, make, model) => {
    if (!category || !make || !model) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/vehicle-templates/match?vehicle_category=${encodeURIComponent(category)}&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data && res.data.axle_config) {
        setAssetForm(prev => ({ ...prev, axle_config: res.data.axle_config }));
        toast.success(`Auto-loaded tyre config for ${make} ${model}`);
      }
    } catch (error) {
      // No template found, that's fine
    }
  };

  const handleCategoryChange = (category) => {
    setAssetForm(prev => ({
      ...prev,
      vehicle_category: category,
      axle_config: TYRE_CATEGORIES.includes(category) ? prev.axle_config : []
    }));
  };

  const handleMakeModelChange = (field, value) => {
    setAssetForm(prev => {
      const updated = { ...prev, [field]: value };
      // Check for template match
      if (updated.vehicle_category && updated.make && updated.model) {
        checkTemplate(updated.vehicle_category, updated.make, updated.model);
      }
      return updated;
    });
  };

  // Axle configuration helpers
  const addAxle = () => {
    setAssetForm(prev => ({
      ...prev,
      axle_config: [
        ...prev.axle_config,
        { position: 'Front', tyre_type: 'Single', tyre_count: 2 }
      ]
    }));
  };

  const updateAxle = (index, field, value) => {
    setAssetForm(prev => {
      const newConfig = [...prev.axle_config];
      newConfig[index] = { ...newConfig[index], [field]: value };
      // Auto-calculate tyre_count
      newConfig[index].tyre_count = newConfig[index].tyre_type === 'Double' ? 4 : 2;
      return { ...prev, axle_config: newConfig };
    });
  };

  const removeAxle = (index) => {
    setAssetForm(prev => ({
      ...prev,
      axle_config: prev.axle_config.filter((_, i) => i !== index)
    }));
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sites`, siteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Site added successfully!');
      setSiteForm({ name: '', location: '' });
      setIsAddSiteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add site');
    }
  };

  const handleEditSite = async (e) => {
    e.preventDefault();
    if (!editingSite) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sites/${editingSite.id}`, {
        name: siteForm.name,
        location: siteForm.location
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Site updated successfully!');
      setSiteForm({ name: '', location: '' });
      setEditingSite(null);
      setIsEditSiteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update site');
    }
  };

  const openEditSite = (site) => {
    setEditingSite(site);
    setSiteForm({ name: site.name, location: site.location || '' });
    setIsEditSiteOpen(true);
  };

  const handleDeleteSite = async (siteId, siteName) => {
    if (!window.confirm(`Delete site "${siteName}" and all its assets?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/sites/${siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Site deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete site');
    }
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...assetForm,
        manufacturing_year: assetForm.manufacturing_year ? parseInt(assetForm.manufacturing_year) : null,
        cum_per_trip: assetForm.cum_per_trip ? parseFloat(assetForm.cum_per_trip) : 0,
        axle_config: TYRE_CATEGORIES.includes(assetForm.vehicle_category) ? assetForm.axle_config : null
      };
      
      await axios.post(`${API}/assets`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset added successfully!');
      resetAssetForm();
      setIsAddAssetOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add asset');
    }
  };

  const handleEditAsset = async (e) => {
    e.preventDefault();
    if (!editingAsset) return;
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        site_id: assetForm.site_id,
        vehicle_category: assetForm.vehicle_category,
        make: assetForm.make,
        model: assetForm.model,
        manufacturing_year: assetForm.manufacturing_year ? parseInt(assetForm.manufacturing_year) : null,
        door_no: assetForm.door_no,
        date_of_purchase: assetForm.date_of_purchase,
        cum_per_trip: assetForm.cum_per_trip ? parseFloat(assetForm.cum_per_trip) : 0,
        axle_config: TYRE_CATEGORIES.includes(assetForm.vehicle_category) ? assetForm.axle_config : null
      };
      
      await axios.put(`${API}/assets/${editingAsset.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset updated successfully!');
      resetAssetForm();
      setEditingAsset(null);
      setIsEditAssetOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update asset');
    }
  };

  const openEditAsset = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      site_id: asset.site_id,
      vehicle_category: asset.vehicle_category || asset.asset_type || '',
      make: asset.make || '',
      model: asset.model || '',
      manufacturing_year: asset.manufacturing_year?.toString() || '',
      door_no: asset.door_no || '',
      date_of_purchase: asset.date_of_purchase || '',
      cum_per_trip: asset.cum_per_trip?.toString() || '',
      axle_config: asset.axle_config || []
    });
    setIsEditAssetOpen(true);
  };

  const resetAssetForm = () => {
    setAssetForm({
      site_id: '',
      vehicle_category: '',
      make: '',
      model: '',
      manufacturing_year: '',
      door_no: '',
      date_of_purchase: '',
      cum_per_trip: '',
      axle_config: []
    });
  };

  const handleDeleteAsset = async (asset) => {
    if (canApprove) {
      if (!window.confirm('Delete this asset?')) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/assets/${asset.id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Asset deleted!');
        fetchData();
        fetchPendingDeletions();
      } catch { toast.error('Failed to delete asset'); }
    } else {
      setDeleteTarget({ id: asset.id, display: `Asset: ${asset.door_no} – ${asset.make} ${asset.model}` });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/assets/bulk-import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Imported ${response.data.imported} assets!`);
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      setIsImportOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  // Template management
  const [editingTemplate, setEditingTemplate] = useState(null);

  const handleSaveTemplate = async () => {
    if (!templateForm.vehicle_category || !templateForm.make || !templateForm.model) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (editingTemplate) {
        await axios.put(`${API}/vehicle-templates/${editingTemplate.id}`, templateForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated and applied to all matching assets!');
      } else {
        await axios.post(`${API}/vehicle-templates`, templateForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template saved and applied to all matching assets!');
      }
      setEditingTemplate(null);
      setTemplateForm({ vehicle_category: '', make: '', model: '', axle_config: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      vehicle_category: template.vehicle_category,
      make: template.make,
      model: template.model,
      axle_config: template.axle_config || []
    });
  };

  const handleApplyTemplate = async (templateId, make, model) => {
    if (!window.confirm(`Apply this template's axle configuration to ALL ${make} ${model} assets?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/vehicle-templates/${templateId}/apply`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/vehicle-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const addTemplateAxle = () => {
    setTemplateForm(prev => ({
      ...prev,
      axle_config: [...prev.axle_config, { position: 'Front', tyre_type: 'Single', tyre_count: 2 }]
    }));
  };

  const updateTemplateAxle = (index, field, value) => {
    setTemplateForm(prev => {
      const newConfig = [...prev.axle_config];
      newConfig[index] = { ...newConfig[index], [field]: value };
      newConfig[index].tyre_count = newConfig[index].tyre_type === 'Double' ? 4 : 2;
      return { ...prev, axle_config: newConfig };
    });
  };

  const removeTemplateAxle = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      axle_config: prev.axle_config.filter((_, i) => i !== index)
    }));
  };

  const toggleSite = (siteId) => {
    setExpandedSites(prev => ({ ...prev, [siteId]: !prev[siteId] }));
  };

  const getAssetsForSite = (siteId) => {
    let filtered = assets.filter(a => a.site_id === siteId);
    
    // Apply filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(a => 
        a.door_no?.toLowerCase().includes(searchLower) ||
        a.make?.toLowerCase().includes(searchLower) ||
        a.model?.toLowerCase().includes(searchLower)
      );
    }
    if (filters.category) {
      filtered = filtered.filter(a => (a.vehicle_category || a.asset_type) === filters.category);
    }
    if (filters.make) {
      filtered = filtered.filter(a => a.make === filters.make);
    }
    
    return filtered;
  };

  // Get unique makes for filter dropdown
  const uniqueMakes = useMemo(() => {
    const makes = [...new Set(assets.map(a => a.make).filter(Boolean))];
    return makes.sort();
  }, [assets]);

  // Get sites filtered by site filter
  const filteredSites = useMemo(() => {
    if (!filters.site) return sites;
    return sites.filter(s => s.id === filters.site);
  }, [sites, filters.site]);

  // Get total filtered asset count
  const totalFilteredAssets = useMemo(() => {
    return filteredSites.reduce((count, site) => count + getAssetsForSite(site.id).length, 0);
  }, [filteredSites, filters]);

  const hasTyres = (category) => TYRE_CATEGORIES.includes(category);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Asset Management</h1>
          <p className="text-slate-600 mt-1">Manage sites and assets across your operations</p>
        </div>
        <div className="flex gap-2">
          {canEdit && user?.is_admin && (
            <Button variant="outline" onClick={() => setIsTemplateOpen(true)} data-testid="manage-templates-btn">
              <Settings2 className="h-4 w-4 mr-2" />
              Vehicle Templates
            </Button>
          )}
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => setIsImportOpen(true)} data-testid="import-assets-btn">
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button variant="outline" onClick={() => setIsAddSiteOpen(true)} data-testid="add-site-btn">
                <Building2 className="h-4 w-4 mr-2" />
                Add Site
              </Button>
              <Button onClick={() => { resetAssetForm(); setIsAddAssetOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-asset-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Asset
              </Button>
            </>
          )}
        </div>
      </div>

      <PendingDeletionsPanel module="assets" requests={pendingDelRequests} onApprove={handleApproveDelete} onReject={handleRejectDelete} />

      {/* Filters */}
      <FilterBar 
        onReset={resetFilters} 
        activeFiltersCount={countActiveFilters(filters)}
      >
        <SearchFilter
          label="Search"
          value={filters.search}
          onChange={(val) => setFilters({ ...filters, search: val })}
          placeholder="Door No, Make, Model..."
        />
        <SelectFilter
          label="Site"
          value={filters.site}
          onChange={(val) => setFilters({ ...filters, site: val })}
          options={sites.map(s => ({ value: s.id, label: s.name }))}
        />
        <SelectFilter
          label="Vehicle Category"
          value={filters.category}
          onChange={(val) => setFilters({ ...filters, category: val })}
          options={VEHICLE_CATEGORIES.map(c => ({ value: c, label: c }))}
        />
        <SelectFilter
          label="Make"
          value={filters.make}
          onChange={(val) => setFilters({ ...filters, make: val })}
          options={uniqueMakes.map(m => ({ value: m, label: m }))}
        />
      </FilterBar>

      {/* Total Count */}
      {countActiveFilters(filters) > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">
            Showing {totalFilteredAssets} of {assets.length} assets
          </span>
          <ExportButton
            rows={assets.filter(a => filteredSites.some(s => s.id === a.site_id))}
            columns={[
              { header: 'Site', key: r => sites.find(s => s.id === r.site_id)?.name || r.site_id },
              { header: 'Door No.', key: 'door_no' },
              { header: 'Category', key: 'vehicle_category' },
              { header: 'Make', key: 'make' },
              { header: 'Model', key: 'model' },
              { header: 'Year', key: 'manufacturing_year' },
              { header: 'Date of Purchase', key: 'date_of_purchase' },
              { header: 'Cum/Trip (m³)', key: 'cum_per_trip' },
              { header: 'Axles', key: r => r.axle_config?.length || 0 },
            ]}
            filename={`assets_${new Date().toISOString().slice(0,10)}`}
            title="Asset Register"
            subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
          />
        </div>
      )}

      {/* Sites and Assets List */}
      <div className="space-y-4">
        {filteredSites.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">{sites.length === 0 ? 'No Sites Yet' : 'No Results'}</h3>
            <p className="text-slate-500 mb-4">{sites.length === 0 ? 'Get started by adding your first site' : 'Try adjusting your filters'}</p>
            {canEdit && sites.length === 0 && (
              <Button onClick={() => setIsAddSiteOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Site
              </Button>
            )}
          </div>
        ) : (
          filteredSites.map(site => {
            const siteAssets = getAssetsForSite(site.id);
            const isExpanded = expandedSites[site.id];
            
            return (
              <div key={site.id} className="bg-white rounded-lg border shadow-sm overflow-hidden">
                {/* Site Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => toggleSite(site.id)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                    <Building2 className="h-5 w-5 text-slate-600" />
                    <div>
                      <h3 className="font-semibold text-slate-900">{site.name}</h3>
                      <p className="text-sm text-slate-500">{site.location || 'No location'} • {siteAssets.length} assets</p>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); openEditSite(site); }}
                        className="text-slate-600 hover:text-slate-800"
                        title="Edit site"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleDeleteSite(site.id, site.name); }}
                        className="text-red-600 hover:text-red-700"
                        title="Delete site"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Assets Table */}
                {isExpanded && (
                  <div className="border-t">
                    {siteAssets.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        No assets in this site yet
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Door No</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Make / Model</TableHead>
                            <TableHead>Year</TableHead>
                            <TableHead>Tyres</TableHead>
                            <TableHead>Cum/Trip</TableHead>
                            <TableHead className="w-20">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {siteAssets.map(asset => {
                            const tyrePositions = generatePositionsPreview(asset.axle_config);
                            return (
                              <TableRow key={asset.id} data-testid={`asset-row-${asset.door_no}`}>
                                <TableCell className="font-medium">{asset.door_no}</TableCell>
                                <TableCell>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    hasTyres(asset.vehicle_category || asset.asset_type) 
                                      ? 'bg-blue-100 text-blue-700' 
                                      : 'bg-slate-100 text-slate-600'
                                  }`}>
                                    {asset.vehicle_category || asset.asset_type}
                                  </span>
                                </TableCell>
                                <TableCell>{asset.make} {asset.model}</TableCell>
                                <TableCell>{asset.manufacturing_year || '-'}</TableCell>
                                <TableCell>
                                  {hasTyres(asset.vehicle_category || asset.asset_type) ? (
                                    tyrePositions.length > 0 ? (
                                      <div className="flex items-center gap-1">
                                        <CircleDot className="h-3 w-3 text-slate-500" />
                                        <span className="text-sm">{tyrePositions.length} positions</span>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-amber-600">Not configured</span>
                                    )
                                  ) : (
                                    <span className="text-xs text-slate-400">N/A (tracked)</span>
                                  )}
                                </TableCell>
                                <TableCell>{asset.cum_per_trip || '-'}</TableCell>
                                <TableCell>
                                  {canEdit && (
                                    <div className="flex items-center gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => openEditAsset(asset)}
                                        className="text-slate-600 hover:text-slate-800"
                                        title="Edit asset"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                      onClick={() => handleDeleteAsset(asset)}
                                        className="text-red-600 hover:text-red-700"
                                        title="Delete asset"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Site Modal */}
      <Dialog open={isAddSiteOpen} onOpenChange={setIsAddSiteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Site</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSite} className="space-y-4">
            <div>
              <Label>Site Name *</Label>
              <Input
                value={siteForm.name}
                onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                placeholder="e.g., Open Cast Mine A"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={siteForm.location}
                onChange={(e) => setSiteForm({ ...siteForm, location: e.target.value })}
                placeholder="e.g., Jharkhand"
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAddSiteOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Site</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Site Modal */}
      <Dialog open={isEditSiteOpen} onOpenChange={setIsEditSiteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Site</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSite} className="space-y-4">
            <div>
              <Label>Site Name *</Label>
              <Input
                value={siteForm.name}
                onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                placeholder="e.g., Open Cast Mine A"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={siteForm.location}
                onChange={(e) => setSiteForm({ ...siteForm, location: e.target.value })}
                placeholder="e.g., Jharkhand"
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsEditSiteOpen(false); setEditingSite(null); }}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Asset Modal */}
      <Dialog open={isAddAssetOpen} onOpenChange={setIsAddAssetOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAsset} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Site *</Label>
                <SearchableSelect
                  value={assetForm.site_id}
                  onValueChange={(val) => setAssetForm({ ...assetForm, site_id: val })}
                  options={sites.map(site => ({ value: site.id, label: site.name }))}
                  placeholder="Select site"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Vehicle Category *</Label>
                <Select value={assetForm.vehicle_category} onValueChange={(val) => {
                  if (val === '__add_new__') { setShowAddCategory(true); return; }
                  handleCategoryChange(val);
                }}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="__add_new__" className="text-blue-600 font-medium border-t">
                      + Add new category...
                    </SelectItem>
                  </SelectContent>
                </Select>
                {showAddCategory && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newCategoryInput}
                      onChange={(e) => setNewCategoryInput(e.target.value)}
                      placeholder="New category name..."
                      className="text-sm h-8"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                      autoFocus
                    />
                    <Button type="button" size="sm" onClick={handleAddCategory} className="h-8 bg-blue-600 hover:bg-blue-700">Add</Button>
                    <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => { setShowAddCategory(false); setNewCategoryInput(''); }}>Cancel</Button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Make *</Label>
                <Input
                  value={assetForm.make}
                  onChange={(e) => handleMakeModelChange('make', e.target.value)}
                  placeholder="e.g., Volvo, Tata, CAT"
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Model *</Label>
                <Input
                  value={assetForm.model}
                  onChange={(e) => handleMakeModelChange('model', e.target.value)}
                  placeholder="e.g., FMX 460"
                  required
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Manufacturing Year</Label>
                <SearchableSelect
                  value={assetForm.manufacturing_year}
                  onValueChange={(val) => setAssetForm({ ...assetForm, manufacturing_year: val })}
                  options={YEAR_OPTIONS.map(year => ({ value: year.toString(), label: year.toString() }))}
                  placeholder="Year"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Door No *</Label>
                <Input
                  value={assetForm.door_no}
                  onChange={(e) => setAssetForm({ ...assetForm, door_no: e.target.value })}
                  placeholder="e.g., D-001"
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Date of Purchase</Label>
                <Input
                  type="date"
                  value={assetForm.date_of_purchase}
                  onChange={(e) => setAssetForm({ ...assetForm, date_of_purchase: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label>Cum per Trip (m³)</Label>
              <Input
                type="number"
                step="0.1"
                value={assetForm.cum_per_trip}
                onChange={(e) => setAssetForm({ ...assetForm, cum_per_trip: e.target.value })}
                placeholder="Volume capacity per trip"
                className="mt-1.5"
              />
            </div>

            {/* Axle Configuration - Only for tyre vehicles */}
            {hasTyres(assetForm.vehicle_category) && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base font-semibold">Tyre Configuration</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Configure axles for tyre positions</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addAxle}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Axle
                  </Button>
                </div>

                {assetForm.axle_config.length === 0 ? (
                  <div className="text-center py-4 bg-slate-50 rounded-lg border border-dashed">
                    <CircleDot className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No axles configured</p>
                    <p className="text-xs text-slate-400">Click "Add Axle" to configure tyre positions</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assetForm.axle_config.map((axle, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Axle Position</Label>
                            <Select value={axle.position} onValueChange={(val) => updateAxle(index, 'position', val)}>
                              <SelectTrigger className="mt-1 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Front">Front</SelectItem>
                                <SelectItem value="Rear">Rear</SelectItem>
                                <SelectItem value="Middle">Middle</SelectItem>
                                <SelectItem value="Tag">Tag</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Tyre on Each End</Label>
                            <Select value={axle.tyre_type} onValueChange={(val) => updateAxle(index, 'tyre_type', val)}>
                              <SelectTrigger className="mt-1 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Single">Single</SelectItem>
                                <SelectItem value="Double">Double</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Total Tyres</Label>
                            <div className="mt-1 h-8 flex items-center px-3 bg-white rounded border text-sm font-medium">
                              {axle.tyre_type === 'Double' ? '4' : '2'}
                            </div>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAxle(index)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    
                    {/* Position Preview */}
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs font-medium text-blue-700 mb-2">Tyre Positions Preview:</p>
                      <div className="flex flex-wrap gap-1">
                        {generatePositionsPreview(assetForm.axle_config).map((pos, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            {pos}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsAddAssetOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Asset</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vehicle Templates Modal - Admin Only */}
      <Dialog open={isTemplateOpen} onOpenChange={(open) => { setIsTemplateOpen(open); if (!open) { setEditingTemplate(null); setTemplateForm({ vehicle_category: '', make: '', model: '', axle_config: [] }); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vehicle Templates</DialogTitle>
          </DialogHeader>
          
          {/* Existing Templates */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3">Saved Templates</h3>
            <p className="text-xs text-slate-500 mb-2">Changes to a template automatically update <strong>all matching assets</strong></p>
            {templates.length === 0 ? (
              <p className="text-sm text-slate-500">No templates yet</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {templates.map(t => (
                  <div key={t.id} className={`flex items-center justify-between p-3 rounded-lg border ${editingTemplate?.id === t.id ? 'bg-blue-50 border-blue-200' : 'bg-slate-50'}`}>
                    <div>
                      <p className="font-medium text-sm">{t.make} {t.model}</p>
                      <p className="text-xs text-slate-500">
                        {t.vehicle_category} • {t.axle_config?.length || 0} axles • {t.axle_config?.reduce((sum, a) => sum + (a.tyre_count || 0), 0) || 0} tyres
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditTemplate(t)}
                        className="text-blue-600 hover:text-blue-700 text-xs"
                        title="Edit template"
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleApplyTemplate(t.id, t.make, t.model)}
                        className="text-green-600 hover:text-green-700 text-xs"
                        title="Apply to all matching assets"
                      >
                        Apply All
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(t.id)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add / Edit Template Form */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">{editingTemplate ? `Editing: ${editingTemplate.make} ${editingTemplate.model}` : 'Add New Template'}</h3>
              {editingTemplate && (
                <Button variant="ghost" size="sm" className="text-slate-500 text-xs" onClick={() => { setEditingTemplate(null); setTemplateForm({ vehicle_category: '', make: '', model: '', axle_config: [] }); }}>
                  Cancel Edit
                </Button>
              )}
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={templateForm.vehicle_category} onValueChange={(val) => setTemplateForm({ ...templateForm, vehicle_category: val })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {TYRE_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Make</Label>
                  <Input
                    value={templateForm.make}
                    onChange={(e) => setTemplateForm({ ...templateForm, make: e.target.value })}
                    placeholder="e.g., Volvo"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Model</Label>
                  <Input
                    value={templateForm.model}
                    onChange={(e) => setTemplateForm({ ...templateForm, model: e.target.value })}
                    placeholder="e.g., FMX 460"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Axle Configuration</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addTemplateAxle}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Axle
                  </Button>
                </div>
                
                {templateForm.axle_config.map((axle, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <Select value={axle.position} onValueChange={(val) => updateTemplateAxle(index, 'position', val)}>
                      <SelectTrigger className="w-28 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Front">Front</SelectItem>
                        <SelectItem value="Rear">Rear</SelectItem>
                        <SelectItem value="Middle">Middle</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={axle.tyre_type} onValueChange={(val) => updateTemplateAxle(index, 'tyre_type', val)}>
                      <SelectTrigger className="w-28 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single">Single (2)</SelectItem>
                        <SelectItem value="Double">Double (4)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeTemplateAxle(index)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button onClick={handleSaveTemplate} className="w-full bg-slate-900 hover:bg-slate-800">
                {editingTemplate ? 'Update Template & Apply to All Assets' : 'Save Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Modal */}
      <Dialog open={isEditAssetOpen} onOpenChange={(open) => { if (!open) { setIsEditAssetOpen(false); setEditingAsset(null); resetAssetForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Asset — {editingAsset?.door_no}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditAsset} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Site *</Label>
                <SearchableSelect
                  value={assetForm.site_id}
                  onValueChange={(val) => setAssetForm({ ...assetForm, site_id: val })}
                  options={sites.map(site => ({ value: site.id, label: site.name }))}
                  placeholder="Select site"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Vehicle Category *</Label>
                <Select value={assetForm.vehicle_category} onValueChange={(val) => {
                  if (val === '__add_new__') { setShowAddCategory(true); return; }
                  handleCategoryChange(val);
                }}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="__add_new__" className="text-blue-600 font-medium border-t">
                      + Add new category...
                    </SelectItem>
                  </SelectContent>
                </Select>
                {showAddCategory && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newCategoryInput}
                      onChange={(e) => setNewCategoryInput(e.target.value)}
                      placeholder="New category name..."
                      className="text-sm h-8"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                      autoFocus
                    />
                    <Button type="button" size="sm" onClick={handleAddCategory} className="h-8 bg-blue-600 hover:bg-blue-700">Add</Button>
                    <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => { setShowAddCategory(false); setNewCategoryInput(''); }}>Cancel</Button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Make *</Label>
                <Input
                  value={assetForm.make}
                  onChange={(e) => handleMakeModelChange('make', e.target.value)}
                  placeholder="e.g., Volvo, Tata, CAT"
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Model *</Label>
                <Input
                  value={assetForm.model}
                  onChange={(e) => handleMakeModelChange('model', e.target.value)}
                  placeholder="e.g., FMX 460"
                  required
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Manufacturing Year</Label>
                <SearchableSelect
                  value={assetForm.manufacturing_year}
                  onValueChange={(val) => setAssetForm({ ...assetForm, manufacturing_year: val })}
                  options={YEAR_OPTIONS.map(year => ({ value: year.toString(), label: year.toString() }))}
                  placeholder="Year"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Door No *</Label>
                <Input
                  value={assetForm.door_no}
                  onChange={(e) => setAssetForm({ ...assetForm, door_no: e.target.value })}
                  placeholder="e.g., D-001"
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Date of Purchase</Label>
                <Input
                  type="date"
                  value={assetForm.date_of_purchase}
                  onChange={(e) => setAssetForm({ ...assetForm, date_of_purchase: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label>Cum per Trip (m³)</Label>
              <Input
                type="number"
                step="0.1"
                value={assetForm.cum_per_trip}
                onChange={(e) => setAssetForm({ ...assetForm, cum_per_trip: e.target.value })}
                placeholder="Volume capacity per trip"
                className="mt-1.5"
              />
            </div>

            {/* Axle Configuration - Only for tyre vehicles */}
            {hasTyres(assetForm.vehicle_category) && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base font-semibold">Tyre Configuration</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Configure axles for tyre positions</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addAxle}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Axle
                  </Button>
                </div>

                {assetForm.axle_config.length === 0 ? (
                  <div className="text-center py-4 bg-slate-50 rounded-lg border border-dashed">
                    <CircleDot className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No axles configured</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assetForm.axle_config.map((axle, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Axle Position</Label>
                            <Select value={axle.position} onValueChange={(val) => updateAxle(index, 'position', val)}>
                              <SelectTrigger className="mt-1 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Front">Front</SelectItem>
                                <SelectItem value="Rear">Rear</SelectItem>
                                <SelectItem value="Middle">Middle</SelectItem>
                                <SelectItem value="Tag">Tag</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Tyre on Each End</Label>
                            <Select value={axle.tyre_type} onValueChange={(val) => updateAxle(index, 'tyre_type', val)}>
                              <SelectTrigger className="mt-1 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Single">Single</SelectItem>
                                <SelectItem value="Double">Double</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Total Tyres</Label>
                            <div className="mt-1 h-8 flex items-center px-3 bg-white rounded border text-sm font-medium">
                              {axle.tyre_type === 'Double' ? '4' : '2'}
                            </div>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAxle(index)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs font-medium text-blue-700 mb-2">Tyre Positions Preview:</p>
                      <div className="flex flex-wrap gap-1">
                        {generatePositionsPreview(assetForm.axle_config).map((pos, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            {pos}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setIsEditAssetOpen(false); setEditingAsset(null); resetAssetForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="edit-asset-submit-btn">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Assets</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload a CSV or Excel file with columns: site_name, vehicle_category, make, model, door_no, date_of_purchase
            </p>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                disabled={uploading}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-slate-400 mt-1">CSV, XLS, XLSX</p>
                  </>
                )}
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteRequestDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        module="assets"
        entryId={deleteTarget?.id}
        entryDisplay={deleteTarget?.display}
        onSuccess={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AssetManagement;
